import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppFrame, TopBar, BottomNav } from "@/components/Shell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Package, Tag, Settings as SettingsIcon, LogOut, Send, QrCode, Boxes, UploadCloud, FileEdit, Users } from "lucide-react";

export default function AdminDashboard() {
  const nav = useNavigate();
  const { logout, email, isAdmin } = useAuth();
  const [counts, setCounts] = useState({ products: 0, categories: 0, customers: 0, drafts: 0 });
  const [inv, setInv] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, c, cu, iv, dr] = await Promise.all([
        api.get("/products", { params: { limit: 500, status: "all" } }),
        api.get("/categories"),
        isAdmin ? api.get("/customers").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        api.get("/inventory/summary").catch(() => ({ data: null })),
        api.get("/products", { params: { status: "draft", limit: 200 } }).catch(() => ({ data: [] })),
      ]);
      setCounts({ products: p.data.length, categories: c.data.length, customers: cu.data.length, drafts: dr.data.length });
      setInv(iv.data);
    })();
  }, [isAdmin]);

  const staffTiles = [
    { to: "/admin/add", icon: Plus, label: "Add Product", desc: "Scan · Speak · Save", primary: true, id: "add-product" },
    { to: "/admin/bulk-upload", icon: UploadCloud, label: "Bulk Upload", desc: "Up to 50 images", id: "bulk-upload", accent: true },
    { to: "/admin/drafts", icon: FileEdit, label: "Drafts", desc: `${counts.drafts} pending`, id: "drafts" },
    { to: "/admin/products", icon: Package, label: "Products", desc: `${counts.products} items`, id: "products" },
    { to: "/admin/inventory", icon: Boxes, label: "Inventory", desc: inv ? `${inv.low_stock} low · ${inv.out_of_stock} out` : "Sales & stock", id: "inventory" },
  ];
  const adminOnlyTiles = [
    { to: "/admin/categories", icon: Tag, label: "Categories", desc: `${counts.categories} total`, id: "categories" },
    { to: "/admin/staff", icon: Users, label: "Team", desc: "Manage staff", id: "staff" },
    { to: "/admin/broadcast", icon: Send, label: "Broadcast", desc: `${counts.customers} customers`, id: "broadcast" },
    { to: "/admin/qr", icon: QrCode, label: "Shop QR", desc: "Print for counter", id: "qr" },
    { to: "/admin/settings", icon: SettingsIcon, label: "Settings", desc: "WhatsApp & store", id: "settings" },
  ];
  const tiles = isAdmin ? [...staffTiles, ...adminOnlyTiles] : staffTiles;

  return (
    <AppFrame>
      <TopBar
        title="Admin"
        subtitle={email}
        onBack={() => nav("/")}
        right={
          <button
            data-testid="admin-logout-btn"
            onClick={() => { logout(); nav("/"); }}
            className="w-9 h-9 rounded-full grid place-items-center border border-black/10 press"
          >
            <LogOut size={16} strokeWidth={1.6} />
          </button>
        }
      />
      <div className="px-5 mt-6">
        <div className="font-display text-3xl leading-tight">Butterfly Studio</div>
        <p className="text-sm text-neutral-500 mt-1">Add products in under 30 seconds using AI.</p>
      </div>
      <div className="px-5 mt-6 grid grid-cols-2 gap-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          const cls = t.primary
            ? "bg-neutral-900 text-white"
            : t.accent
              ? "bg-[#FADADD]/70 border border-[#FADADD]"
              : "bg-white border border-black/5 shadow-[0_4px_20px_-6px_rgba(0,0,0,0.06)]";
          return (
            <Link
              key={t.id}
              to={t.to}
              data-testid={`admin-tile-${t.id}`}
              className={`aspect-square rounded-3xl press p-5 flex flex-col justify-between ${cls}`}
            >
              <Icon size={26} strokeWidth={1.5} className={t.primary ? "opacity-90" : "text-neutral-700"} />
              <div>
                <div className="font-display text-xl leading-tight">{t.label}</div>
                <div className={`text-[11px] mt-1 ${t.primary ? "opacity-70" : "text-neutral-500"}`}>{t.desc}</div>
              </div>
            </Link>
          );
        })}
      </div>
      <BottomNav />
    </AppFrame>
  );
}
